/**
 * The account's second factor, as it actually is.
 *
 * What stood here before was a green "Enabled" badge with nothing behind it -
 * it said the account was protected by 2FA whether or not it was. A security
 * indicator that cannot be wrong is worse than none, because it stops people
 * looking. Everything shown here is read from the server.
 *
 * The rule itself is not re-implemented on this side: `secondFactor` comes from
 * the same endpoint the login flow consults, so the card cannot claim a factor
 * the sign-in does not ask for.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Fingerprint,
  Mail,
  Plus,
  Shield,
  ShieldOff,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import {
  isUserCancellation,
  passkeyService,
  type PasskeyState,
} from "../../services/passkey.service";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { twoFactorService } from "../../services/twoFactor.service";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { isReauthRequired, signInAgain } from "../../utils/reauthRequired";

const QUERY_KEY = ["auth", "passkeys"];

/** The name a new passkey is stored under; the user's language at creation. */
function defaultLabel(t: Translation): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return t.settings2DeviceAndroid;
  if (/Windows/.test(ua)) return t.settings2DeviceWindows;
  return t.settings2DeviceThis;
}

export const SecondFactorCard: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [supported, setSupported] = useState(false);
  const [problem, setProblem] = useState("");
  /** Set once the code has been sent; holds the pending session. */
  const [pendingToken, setPendingToken] = useState("");
  const [codeEntry, setCodeEntry] = useState("");
  /**
   * Set when the server wants a fresh sign-in before changing a sign-in method.
   * Shown instead of `problem`, because the answer is an action, not a retry.
   */
  const [reauthNeeded, setReauthNeeded] = useState(false);

  /** True when the failure was the step-up check, which this card then shows. */
  const needsReauth = (error: unknown): boolean => {
    if (!isReauthRequired(error)) return false;
    setProblem("");
    setReauthNeeded(true);
    return true;
  };

  useEffect(() => {
    setSupported(passkeyService.isSupported());
  }, []);

  const state = useQuery<PasskeyState>({
    queryKey: QUERY_KEY,
    queryFn: () => passkeyService.list(),
  });

  const enrol = useMutation({
    mutationFn: () => passkeyService.enrol(defaultLabel(t)),
    onSuccess: () => {
      setProblem("");
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: unknown) => {
      // A dismissed prompt is not an error worth shouting about.
      if (isUserCancellation(error)) {
        setProblem("");
        return;
      }
      if (needsReauth(error)) return;
      setProblem(t.settings2PasskeyAddFailed);
    },
  });

  const remove = useMutation({
    mutationFn: (credentialId: string) => passkeyService.remove(credentialId),
    onSuccess: () => {
      setProblem("");
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: unknown) => {
      if (needsReauth(error)) return;
      const message = (
        error as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      setProblem(message || t.settings2PasskeyRemoveFailed);
    },
  });

  /**
   * Step one. Nothing is switched on here - the server sends a code and waits
   * to be told it arrived, so an address that cannot receive mail never becomes
   * a login requirement.
   */
  const startEmail = useMutation({
    mutationFn: () => twoFactorService.startEmail(),
    onSuccess: (result) => {
      setProblem("");
      setPendingToken(result.mfaToken);
    },
    onError: (error: unknown) => {
      const code = (
        error as { response?: { data?: { error?: { code?: string } } } }
      )?.response?.data?.error?.code;
      setProblem(
        code === "DELIVERY_FAILED"
          ? t.settings2EmailDeliveryFailed
          : code === "NO_EMAIL_ADDRESS"
            ? t.settings2EmailNoAddress
            : t.settings2EmailSetupFailed,
      );
    },
  });

  const confirmEmail = useMutation({
    mutationFn: () => twoFactorService.confirmEmail(pendingToken, codeEntry),
    onSuccess: async () => {
      setProblem("");
      setPendingToken("");
      setCodeEntry("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => setProblem(t.settings2CodeInvalid),
  });

  const disableEmail = useMutation({
    mutationFn: () => twoFactorService.disableEmail(),
    onSuccess: async () => {
      setProblem("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: unknown) => {
      if (needsReauth(error)) return;
      setProblem(t.settings2SettingChangeFailed);
    },
  });

  if (state.isLoading) {
    return (
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600">
        {t.settings2CheckingSecurity}
      </div>
    );
  }

  if (state.isError || !state.data) {
    // Silence would read as "nothing to see here", which is the one thing this
    // card must never imply.
    return (
      <div
        className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800"
        data-testid="second-factor-unavailable"
      >
        {t.settings2SecurityLoadFailed}
      </div>
    );
  }

  const { passkeys, secondFactor, emailAddress } = state.data;
  const usingPasskeys = secondFactor === "passkey";
  const usingEmail = secondFactor === "email";

  return (
    <div className="mb-6" data-testid="second-factor-card">
      <div
        className={`flex items-start justify-between p-4 rounded-lg border ${
          secondFactor === "none"
            ? "bg-gray-50 border-gray-200"
            : "bg-sky-50 border-sky-200"
        }`}
      >
        <div className="flex items-start space-x-3">
          {usingPasskeys ? (
            <Fingerprint className="w-5 h-5 text-sky-600 mt-0.5" />
          ) : usingEmail ? (
            <Mail className="w-5 h-5 text-sky-600 mt-0.5" />
          ) : (
            <ShieldOff className="w-5 h-5 text-gray-500 mt-0.5" />
          )}
          <div>
            <span className="text-sm font-medium text-sky-900 block">
              {t.settings2TwoFactorTitle}
            </span>
            <span
              className="text-xs text-sky-700"
              data-testid="second-factor-description"
            >
              {usingPasskeys
                ? t.settings2FactorPasskeyDescription
                : usingEmail
                  ? emailAddress
                    ? t.settings2FactorEmailToDescription.replace(
                        "{email}",
                        emailAddress,
                      )
                    : t.settings2FactorEmailDescription
                  : t.settings2FactorNoneDescription}
            </span>
          </div>
        </div>
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800 whitespace-nowrap"
          data-testid="second-factor-badge"
        >
          {usingPasskeys
            ? t.settings2BadgePasskey
            : usingEmail
              ? t.settings2BadgeEmailCode
              : t.settings2BadgeNotEnabled}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
            <Shield className="w-4 h-4" />
            <span>{t.settings2Passkeys}</span>
          </h3>
          {supported && (
            <button
              type="button"
              onClick={() => enrol.mutate()}
              disabled={enrol.isPending}
              data-testid="add-passkey-button"
              className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-900 disabled:opacity-60"
            >
              <Plus className="w-4 h-4" />
              {enrol.isPending
                ? t.settings2WaitingForDevice
                : t.settings2AddPasskey}
            </button>
          )}
        </div>

        {!supported && (
          <p className="text-xs text-gray-500 mb-3">
            {t.settings2PasskeysUnsupported}
          </p>
        )}

        {reauthNeeded && (
          <div
            className="flex items-center justify-between gap-3 p-3 mb-3 bg-amber-50 rounded-lg border border-amber-200"
            data-testid="second-factor-reauth"
            role="alert"
          >
            <p className="text-sm text-amber-800">{t.settings2ReauthMessage}</p>
            <button
              type="button"
              onClick={() =>
                void signInAgain(
                  window.location.pathname + window.location.search,
                )
              }
              data-testid="second-factor-sign-in-again"
              className="px-3 py-1.5 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 whitespace-nowrap"
            >
              {t.settings2SignInAgain}
            </button>
          </div>
        )}

        {problem && (
          <p
            className="text-sm text-red-600 mb-3"
            data-testid="second-factor-problem"
          >
            {problem}
          </p>
        )}

        {passkeys.length === 0 ? (
          <p className="text-sm text-gray-600">{t.settings2NoPasskeys}</p>
        ) : (
          <ul className="space-y-2" data-testid="passkey-list">
            {passkeys.map((passkey) => (
              <li
                key={passkey.credentialId}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900 block">
                    {passkey.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t.settings2PasskeyAdded.replace(
                      "{date}",
                      fmt.date(passkey.createdAt),
                    )}
                    {" · "}
                    {passkey.lastUsedAt
                      ? t.settings2PasskeyLastUsed.replace(
                          "{date}",
                          fmt.date(passkey.lastUsedAt),
                        )
                      : t.settings2PasskeyNotUsed}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => remove.mutate(passkey.credentialId)}
                  disabled={remove.isPending}
                  aria-label={t.settings2RemovePasskey.replace(
                    "{label}",
                    passkey.label,
                  )}
                  className="text-gray-400 hover:text-red-600 disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {passkeys.length === 1 && (
          <p className="text-xs text-gray-500 mt-3">
            {t.settings2SecondPasskeyHint}
          </p>
        )}
      </div>

      <div className="mt-6" data-testid="email-code-section">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center space-x-2 mb-3">
          <Mail className="w-4 h-4" />
          <span>{t.settings2BadgeEmailCode}</span>
        </h3>

        {usingPasskeys ? (
          <p className="text-sm text-gray-600">
            {t.settings2EmailCodeSuperseded}
          </p>
        ) : usingEmail ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {emailAddress
                ? t.settings2EmailCodeSentTo.replace("{email}", emailAddress)
                : t.settings2EmailCodeSentToYours}
            </p>
            <button
              type="button"
              onClick={() => disableEmail.mutate()}
              disabled={disableEmail.isPending}
              data-testid="disable-email-code"
              className="text-sm font-medium text-gray-700 hover:text-red-600 disabled:opacity-60 whitespace-nowrap"
            >
              {disableEmail.isPending
                ? t.settings2TurningOff
                : t.settings2TurnOff}
            </button>
          </div>
        ) : pendingToken ? (
          <div data-testid="email-code-confirm">
            <p className="text-sm text-gray-600 mb-2">
              {t.settings2CodeSentEnter.replace(
                "{email}",
                emailAddress ?? t.settings2YourAddress,
              )}
            </p>
            <div className="flex items-center gap-2">
              <input
                value={codeEntry}
                onChange={(event) =>
                  setCodeEntry(
                    event.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={t.settings2EmailedCode}
                placeholder="123456"
                data-testid="email-code-input"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm tracking-widest"
              />
              <button
                type="button"
                onClick={() => confirmEmail.mutate()}
                disabled={codeEntry.length !== 6 || confirmEmail.isPending}
                data-testid="confirm-email-code"
                className="px-3 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-60"
              >
                {confirmEmail.isPending
                  ? t.settings2Checking
                  : t.settings2Confirm}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingToken("");
                  setCodeEntry("");
                  setProblem("");
                }}
                className="px-2 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600">{t.settings2EmailCodeOffer}</p>
            <button
              type="button"
              onClick={() => startEmail.mutate()}
              disabled={startEmail.isPending}
              data-testid="enable-email-code"
              className="text-sm font-medium text-sky-700 hover:text-sky-900 disabled:opacity-60 whitespace-nowrap"
            >
              {startEmail.isPending
                ? t.settings2SendingCode
                : t.settings2TurnOn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondFactorCard;
